import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { 
  Box, 
  Button, 
  Container, 
  Typography, 
  Paper, 
  CircularProgress,
  Alert, 
  Stack,
  Stepper,
  Step,
  StepLabel,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { useNavigate } from 'react-router-dom';
import { importTransactions } from '../../../services/transactionService';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useGroupContext } from '../../Groups/useGroupContext';

const steps = ['Select File', 'Upload', 'Results'];

const TransactionImportPage = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { getContextParams, isGroupMode, currentGroupName } = useGroupContext();

  const onDrop = useCallback(acceptedFiles => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
      setActiveStep(1);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ 
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.csv', '.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
    },
    maxFiles: 1
  });

  const handleUpload = async () => {
    if (!file) return;
  
    setIsLoading(true);
    setError(null);
  
    try {
      const contextParams = getContextParams();
      const data = await importTransactions(file,contextParams);
      setResult(data);
      setActiveStep(2);
    } catch (err) {
      let errorMessage = 'Failed to import transactions';
      let errorData = null;
      
      if (err.response && err.response.data) {
        errorData = err.response.data;
        
        if (err.response.data.message) {
          errorMessage = err.response.data.message;
        } else if (err.response.data.error) {
          errorMessage = err.response.data.error;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      setResult({
        success: false,
        errors: errorData?.errors || [],
        message: errorData?.message || errorMessage
      });
      setActiveStep(2);
    } finally {
      setIsLoading(false);
    }
  };

  const downloadTemplate = () => {
    const headers = ['date', 'vendor', 'amount', 'category', 'note'];
    const sampleRow = ['2025-05-09', 'Sample Vendor', '19.99', 'Groceries', 'Sample transaction note'];
    
    const csvContent = [
      headers.join(','),
      sampleRow.join(',')
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'transaction_import_template.csv');
    document.body.appendChild(link);
    link.click();
    
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const reset = () => {
    setFile(null);
    setResult(null);
    setError(null);
    setActiveStep(0);
  };

 const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ mt: 4 }}>
            {isGroupMode && (
              <Alert severity="info" sx={{ mb: 3 }}>
                Importing transactions for group: <strong>{currentGroupName}</strong>
              </Alert>
            )}
            
            <Paper
              {...getRootProps()}
              sx={{
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: isDragActive ? 'rgba(25, 118, 210, 0.08)' : 'white',
                border: '2px dashed',
                borderColor: isDragActive ? 'primary.main' : 'grey.400',
                borderRadius: 2,
                transition: 'all 0.3s',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'rgba(25, 118, 210, 0.04)'
                }
              }}
            >
              <input {...getInputProps()} />
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                gap: 2, 
                py: 4 
              }}>
                <CloudUploadIcon sx={{ fontSize: 60, color: 'primary.main' }} />
                <Typography variant="h6">
                  {isDragActive ? 'Drop the CSV or XLSX file here' : 'Drag and drop a CSV or XLSX file here, or click to select file'}
                </Typography>
                  <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    Upload a CSV or Excel file with these columns: <strong>date, vendor, amount, category, note</strong>
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    <strong>Important:</strong> The first row should contain the column headers exactly as shown above.
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    • Date format: yyyy-mm-dd, dd/mm/yyyy, or dd-mm-yyyy<br />
                    • Amount: Use numbers only (e.g., 15.99)<br />
                    • Category: Must match an existing category name in your {isGroupMode ? 'group' : 'account'}<br />
                    • The vendor and note fields are optional
                  </Typography>
                </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    onClick={downloadTemplate}
                    startIcon={<FileDownloadIcon />}
                  >
                    Download Template
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Box>
        );
      
      case 1:
        return (
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>File Selected</Typography>
              <Typography color="textSecondary">{file?.name}</Typography>
              <Typography variant="body2" color="textSecondary">
                {(file?.size / 1024).toFixed(2)} KB
              </Typography>
              
              {isGroupMode && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Importing to group: <strong>{currentGroupName}</strong>
                </Alert>
              )}
              
              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
              
              <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 3 }}>
                <Button 
                  variant="outlined" 
                  onClick={reset}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={handleUpload}
                  disabled={isLoading}
                  startIcon={isLoading ? <CircularProgress size={20} /> : null}
                >
                  {isLoading ? 'Uploading...' : 'Upload and Import'}
                </Button>
              </Stack>
            </Paper>
          </Box>
        );
      
      case 2:
        return (
          <Box sx={{ mt: 4 }}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                {result?.success ? (
                  <CheckCircleIcon color="success" sx={{ mr: 1, fontSize: 28 }} />
                ) : (
                  <ErrorIcon color="error" sx={{ mr: 1, fontSize: 28 }} />
                )}
                <Typography variant="h6">
                  {result?.success 
                    ? 'Import Complete' 
                    : 'Import Failed'}
                </Typography>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="body1" gutterBottom>
                {result?.success 
                  ? `Successfully imported ${result.imported} transactions${isGroupMode ? ` to ${currentGroupName}` : ''}.`
                  : result?.message || 'No transactions were imported.'}
              </Typography>
              
              {result?.errors && result.errors.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                    Errors ({result.errors.length}):
                  </Typography>
                  <List dense sx={{ 
                    bgcolor: 'error.light', 
                    maxHeight: 300, 
                    overflow: 'auto', 
                    borderRadius: 1,
                    opacity: 0.9
                  }}>
                    {result.errors.map((error, index) => (
                      <ListItem key={index}>
                        <ListItemText 
                          primary={error} 
                          primaryTypographyProps={{ 
                            fontWeight: 'medium',
                            color: 'error.contrastText'
                          }} 
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
              
              <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 3 }}>
                <Button 
                  variant="outlined" 
                  onClick={reset}
                >
                  Import Another File
                </Button>
              </Stack>
            </Paper>
          </Box>
        );
      
      default:
        return null;
    }
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        <Typography variant="h4" gutterBottom>
          Import Transactions {isGroupMode && `- ${currentGroupName}`}
        </Typography>
        
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {renderStepContent()}
      </Box>
    </Container>
  );
};

export default TransactionImportPage;